import {Localize} from "@dp-instant/i18n";
import {visibilityEffect} from "@dp-instant/ui";
import {UIConstants} from "@game/ui/constants";
import {apiClient} from "@game/api/ApiClient";
import {playUIClickSound} from "@game/helpers";
import {Images} from "@game/ui/images";
import {tonConnectManager} from "@game/ton/TonConnectManager";
import {tonDepositTracker} from "@game/ton/TonDepositTracker";
import {tributeDepositTracker} from "@game/tribute/TributeDepositTracker";
import {WalletPopup} from "./WalletPopup";
import cn from "classnames";
import {useState, useEffect, useRef} from "preact/hooks";
import "./PaymentMethodPopup.less";

type PaymentMethodPopupProps = {
    onConfirm: (confirmed?: boolean) => void;
    slug: string;
    price: number;
    title: string;
    starsAmount?: number;
    tributePriceUsd?: number; // cents (100 = $1)
    onPaid?: (newHardBalance: number) => void;
};

const STAR_USD = 0.015;
const TON_POLL_INTERVAL = 3000;
const TON_POLL_TIMEOUT = 90000;
const TRIBUTE_POLL_INTERVAL = 3000;
const TRIBUTE_POLL_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export const PaymentMethodPopup = ({onConfirm, slug, price, title, starsAmount, tributePriceUsd, onPaid}: PaymentMethodPopupProps) => {
    const [invisible] = visibilityEffect(UIConstants.popup.startDelay);
    const [busy, setBusy] = useState(false);
    const [tonBusy, setTonBusy] = useState(false);
    const [tonPrice, setTonPrice] = useState<string | null>(null);
    const [tonStatus, setTonStatus] = useState<string | null>(null);
    const [rubRate, setRubRate] = useState<number | null>(null);
    const [tributeBusy, setTributeBusy] = useState(false);
    const [tributeStatus, setTributeStatus] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showWallet, setShowWallet] = useState(false);
    const [currentDepositId, setCurrentDepositId] = useState<number | null>(null);
    const depositIdRef = useRef<number | null>(null);
    const tributeDepositIdRef = useRef<number | null>(null);
    const pollRef = useRef<number | null>(null);
    const tributePollRef = useRef<number | null>(null);
    const balance = apiClient.hardBalance;

    // Fetch TON rate on mount
    useEffect(() => {
        apiClient.getTonRate()
            .then(({rate, rubRate: rub}: any) => {
                const usd = price * STAR_USD;
                const ton = (usd / rate) * 1.03; // +3% buffer
                setTonPrice(ton.toFixed(2));
                if (rub) setRubRate(rub);
            })
            .catch(() => {
                // TON rate unavailable — hide button
            });

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (tributePollRef.current) clearInterval(tributePollRef.current);
        };
    }, []);

    const handleTgStars = async () => {
        if (busy || tonBusy) return;
        playUIClickSound();
        setBusy(true);

        try {
            const result = await apiClient.purchase(slug);

            if (result.method === "balance") {
                apiClient.hardBalance = result.newHardBalance ?? 0;
                apiClient.emitHardBalanceUpdate();
                onPaid?.(apiClient.hardBalance);
                setSuccess(true);
                setTimeout(() => onConfirm(true), 2000);
                return;
            }

            const tg = (window as any).Telegram?.WebApp;
            if (!tg?.openInvoice || !result.url) {
                setBusy(false);
                return;
            }

            tg.openInvoice(result.url, (status: string) => {
                if (status === "paid") {
                    setSuccess(true);
                    // Fetch actual balance from server (handles both top-ups and purchases)
                    apiClient.getMe().catch(() => {}).then(() => {
                        apiClient.emitHardBalanceUpdate();
                        onPaid?.(apiClient.hardBalance);
                    });
                    setTimeout(() => onConfirm(true), 2000);
                } else {
                    apiClient.trackFunnelEvent("purchase_cancelled");
                    setBusy(false);
                }
            });
        } catch {
            setBusy(false);
            const tg = (window as any).Telegram?.WebApp;
            tg?.showAlert?.(Localize.get("ui-payment-error", "Payment error. Please try again."));
        }
    };

    const handleTonPayment = async () => {
        if (busy || tonBusy) return;
        playUIClickSound();
        setTonBusy(true);
        setTonStatus(null);
        setCurrentDepositId(null);

        try {
            // 1. Create deposit on server
            const deposit = await apiClient.purchaseTon(slug);
            setCurrentDepositId(deposit.depositId);
            depositIdRef.current = deposit.depositId;

            // 2. Connect wallet if needed + send transaction
            setTonStatus(Localize.get("ui-ton-connecting", "Connecting wallet..."));
            await tonConnectManager.sendTransaction(
                deposit.walletAddress,
                deposit.tonNano,
                deposit.memo
            );

            // 3. Poll for completion
            setTonStatus(Localize.get("ui-ton-waiting", "Waiting for confirmation..."));
            const startTime = Date.now();

            pollRef.current = window.setInterval(async () => {
                try {
                    const status = await apiClient.getTonDepositStatus(deposit.depositId);

                    if (status.status === "completed") {
                        if (pollRef.current) clearInterval(pollRef.current);
                        pollRef.current = null;
                        // Refresh profile to get updated balance
                        try { await apiClient.getMe(); } catch {}
                        apiClient.emitHardBalanceUpdate();
                        onPaid?.(apiClient.hardBalance);
                        // Untrack from background tracker to prevent duplicate alert
                        if (currentDepositId) tonDepositTracker.untrack(currentDepositId);
                        setSuccess(true);
                        setTimeout(() => onConfirm(true), 2000);
                        return;
                    }

                    if (status.status === "expired" || Date.now() - startTime > TON_POLL_TIMEOUT) {
                        if (pollRef.current) clearInterval(pollRef.current);
                        pollRef.current = null;
                        setTonBusy(false);
                        setCurrentDepositId(null);
                        setTonStatus(Localize.get("ui-ton-timeout", "Payment timeout. Please try again."));
                    }
                } catch {
                    // continue polling
                }
            }, TON_POLL_INTERVAL);

        } catch (err: any) {
            tonConnectManager.reset();
            setTonBusy(false);
            const msg = err?.message || "";
            const depId = depositIdRef.current;

            // If we have a deposit, the transaction MAY have gone through.
            // Move to background tracking instead of showing an error.
            if (depId && !msg.includes("cancelled") && !msg.includes("Cancelled")) {
                tonDepositTracker.track(depId);
                setCurrentDepositId(null);
                depositIdRef.current = null;
                setTonStatus(null);
                const tg = (window as any).Telegram?.WebApp;
                tg?.showPopup?.({
                    title: Localize.get("ui-ton-checking", "Checking payment"),
                    message: Localize.get("ui-ton-checking-msg", "If you sent the payment, it will be confirmed shortly."),
                    buttons: [{ type: "ok" }],
                });
            } else if (msg.includes("cancelled") || msg.includes("Cancelled") || msg.includes("timeout") || msg.includes("reject") || msg.includes("Reset")) {
                setCurrentDepositId(null);
                depositIdRef.current = null;
                setTonStatus(null);
            } else {
                setCurrentDepositId(null);
                depositIdRef.current = null;
                setTonStatus(Localize.get("ui-ton-error", "TON payment error. Please try again."));
            }
        }
    };

    const handleTributePayment = async () => {
        if (busy || tonBusy || tributeBusy) return;
        playUIClickSound();
        setTributeBusy(true);
        setTributeStatus(null);

        try {
            // 1. Create deposit on server
            const {depositId, payUrl} = await apiClient.purchaseTribute(slug);
            tributeDepositIdRef.current = depositId;

            // 2. Start background tracking immediately (survives popup close)
            tributeDepositTracker.track(depositId);

            // 3. Open Tribute TMA payment page
            const tg = (window as any).Telegram?.WebApp;
            tg?.openTelegramLink?.(payUrl);

            // 4. Poll for completion (in-popup, faster interval)
            setTributeStatus(Localize.get("ui-tribute-waiting", "Waiting for payment..."));
            const startTime = Date.now();

            tributePollRef.current = window.setInterval(async () => {
                try {
                    const status = await apiClient.getTributeDepositStatus(depositId);

                    if (status.status === "completed") {
                        if (tributePollRef.current) clearInterval(tributePollRef.current);
                        tributePollRef.current = null;
                        tributeDepositTracker.untrack(depositId);
                        try { await apiClient.getMe(); } catch {}
                        apiClient.emitHardBalanceUpdate();
                        onPaid?.(apiClient.hardBalance);
                        setSuccess(true);
                        const tg = (window as any).Telegram?.WebApp;
                        const msg = Localize.get("ui-tribute-confirmed", "Your payment has been confirmed!");
                        try {
                            tg?.showPopup?.({
                                title: Localize.get("ui-tribute-received", "Payment received"),
                                message: msg,
                                buttons: [{ type: "ok" }],
                            }, () => onConfirm(true));
                        } catch {
                            setTimeout(() => onConfirm(true), 3000);
                        }
                        return;
                    }

                    if (status.status === "expired" || Date.now() - startTime > TRIBUTE_POLL_TIMEOUT) {
                        if (tributePollRef.current) clearInterval(tributePollRef.current);
                        tributePollRef.current = null;
                        setTributeBusy(false);
                        setTributeStatus(Localize.get("ui-tribute-timeout", "Payment not received. Please try again."));
                    }
                } catch {
                    // continue polling
                }
            }, TRIBUTE_POLL_INTERVAL);
        } catch {
            setTributeBusy(false);
            setTributeStatus(Localize.get("ui-tribute-error", "Payment error. Please try again."));
        }
    };

    const handleCancel = () => {
        playUIClickSound();
        if (tributeBusy && tributeDepositIdRef.current) {
            if (tributePollRef.current) clearInterval(tributePollRef.current);
            tributePollRef.current = null;
            tributeDepositTracker.track(tributeDepositIdRef.current);
        }
        if (tonBusy && currentDepositId) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            tonDepositTracker.track(currentDepositId);
        }
        onConfirm(false);
    };

    if (showWallet) {
        return <WalletPopup onBack={() => setShowWallet(false)} />;
    }

    if (success) {
        const displayAmount = starsAmount ?? price;
        return (
            <div className="popup-payment-success-banner">
                <div className="popup-payment-success-banner__text">+{displayAmount} ⭐</div>
            </div>
        );
    }

    const topupAmount = starsAmount ?? price;
    const newBalance = balance + topupAmount;

    return (
        <div className="popups">
            <div className={cn("popup-payment", {invisible})}>
                <div className="popup-payment__handle" />
                <div className="popup__title">{Localize.get("ui-payment-topup-title", "TOP UP")}</div>
                <div className="popup-payment__body">
                    <div className="popup-payment__item-title">{title}</div>
                    <div className="popup-payment__balance-row">
                        <span className="popup-payment__balance-current">{balance} <Images.Star /></span>
                        <span className="popup-payment__balance-arrow">→</span>
                        <span className="popup-payment__balance-new">{newBalance} <Images.Star /></span>
                    </div>

                    <div className="popup-payment__methods">
                        <button
                            className={cn("btn-green", "popup-payment__method-btn")}
                            disabled={busy || tonBusy || tributeBusy}
                            onClick={handleTgStars}
                        >
                            {busy ? "..." : `${price} ⭐ Telegram Stars`}
                        </button>

                        {tonPrice && (
                            <div className="popup-payment__ton-row">
                                <button
                                    className={cn("btn-ton", "popup-payment__method-btn", "popup-payment__method-btn--ton")}
                                    disabled={busy || tonBusy || tributeBusy}
                                    onClick={handleTonPayment}
                                >
                                    {tonBusy
                                        ? (tonStatus || "...")
                                        : <>~{tonPrice} <Images.Ton /> TON</>
                                    }
                                </button>
                                <button
                                    className="popup-payment__wallet-btn"
                                    onClick={() => { playUIClickSound(); setShowWallet(true); }}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0098EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="6" width="20" height="14" rx="2" />
                                        <path d="M2 10h20" />
                                        <circle cx="17" cy="15" r="1.5" fill="#0098EA" stroke="none" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        <button
                            className={cn("btn-tribute", "popup-payment__method-btn")}
                            disabled={busy || tonBusy || tributeBusy}
                            onClick={handleTributePayment}
                        >
                            {tributeBusy
                                ? (tributeStatus || "...")
                                : (() => {
                                    const isRu = Localize.defaultLocale?.startsWith("ru");
                                    const usdCents = tributePriceUsd || Math.round(price * STAR_USD * 100);
                                    if (isRu && rubRate) {
                                        const rub = Math.ceil(usdCents / 100 * rubRate);
                                        return <><span className="tribute-price">~{rub}₽</span> <Images.Sbp /> СБП</>;
                                    }
                                    if (isRu) {
                                        return <><Images.Sbp /> СБП</>;
                                    }
                                    return <><span className="tribute-price">~${Math.round(usdCents / 100)}</span> Card</>;
                                })()
                            }
                        </button>
                    </div>

                    {tonStatus && !tonBusy && (
                        <div className="popup-payment__ton-status">{tonStatus}</div>
                    )}

                    {tributeStatus && !tributeBusy && (
                        <div className="popup-payment__ton-status">{tributeStatus}</div>
                    )}

                    <button
                        className="popup-payment__cancel"
                        onClick={handleCancel}
                    >
                        {Localize.get("ui-common-cancel", "CANCEL")}
                    </button>
                </div>
            </div>
        </div>
    );
};
